'use client';

import {
  subscribeToMiniCartAnnouncements,
  subscribeToMiniCartWithdrawals,
} from '@lib/commerce/miniCartAnnouncement';
import { useCallback, useEffect, useRef, useState } from 'react';

export type MiniCartAnnouncement = {
  /** Changes with every announcement, so a repeated message is re-read. */
  id: number;
  message: string;
};

/**
 * What the header's mini-cart live region says: the cart count whenever it
 * changes after first appearing, and whatever the island announces (its
 * errors) until it is withdrawn. A count hidden while rate limited is
 * neither news nor a reset.
 */
export const useMiniCartAnnouncement = (
  count: number | undefined,
  formatCount: (count: number) => string
): MiniCartAnnouncement | null => {
  const [announcement, setAnnouncement] = useState<MiniCartAnnouncement | null>(
    null
  );
  const lastIdRef = useRef(0);
  const announce = useCallback((message: string) => {
    lastIdRef.current += 1;
    setAnnouncement({ id: lastIdRef.current, message });
  }, []);

  const formatCountRef = useRef(formatCount);
  formatCountRef.current = formatCount;
  const lastCountRef = useRef(count);
  useEffect(() => {
    if (count === undefined) return;
    const lastCount = lastCountRef.current;
    lastCountRef.current = count;
    if (lastCount !== undefined && lastCount !== count) {
      announce(formatCountRef.current(count));
    }
  }, [count, announce]);

  useEffect(() => subscribeToMiniCartAnnouncements(announce), [announce]);
  useEffect(
    () =>
      subscribeToMiniCartWithdrawals((message) =>
        setAnnouncement((previous) =>
          previous?.message === message ? null : previous
        )
      ),
    []
  );

  return announcement;
};
